(ns dashboard.events
  (:require
   [goog.dom :as dom]
   [ajax.core :as ajax]
   [day8.re-frame.http-fx]
   [day8.re-frame.async-flow-fx]
   [reagent.core :as reagent]
   [re-frame.db :as re-frame-db]
   [re-frame.core :as re-frame]
   [dashboard.cofx]
   [dashboard.config :as config]
   [dashboard.appsync.core :as re-graph]
   [dashboard.utils :refer [graphql-request-format ->json parse-params]]
   [dashboard.db :as db :refer [local-store->]]
   [dashboard.router]
   [dashboard.i18n :refer [translate]]
   [dashboard.interceptors :refer [standard-interceptors ->local-store]]
   [dashboard.events.auth :as auth]
   [dashboard.events.organizations :as organizations]
   [dashboard.events.users :as users]
   [dashboard.events.spaces :as spaces]
   [dashboard.events.google :as google]
   [dashboard.events.auth0 :as auth0 :refer [do-auth!]]
   [dashboard.events.events :as events]
   [dashboard.events.rooms :as rooms]
   [dashboard.events.bookings :as bookings]
   [dashboard.demo-event-organizer :refer [demo-db]]))

;; Navigation

(re-frame/reg-event-fx
 :navigate
 standard-interceptors
 (fn [{:keys [db]} [route route-params query-params]]
   {:navigate! [route route-params query-params]}))

;; Init and Reset DB

(re-frame/reg-event-db
 :set-demo
 [(re-frame/path :settings)
  ->local-store
  standard-interceptors]
 (fn [settings [_]]
   (assoc settings :is-demo? true)))

(re-frame/reg-event-db
 :unset-demo
 [(re-frame/path :settings)
  ->local-store
  standard-interceptors]
 (fn [settings [_]]
   (assoc settings :is-demo? false)))

(re-frame/reg-event-db
 ::initialize-db
 (fn [db [_]]
   (merge db/default-db)))

(re-frame/reg-event-fx
 :reset-db
 standard-interceptors
 (fn [{:keys [db]} _]
   (local-store->)
   {:db db/default-db}))

(re-frame/reg-event-fx
 :reset-demo-db
 standard-interceptors
 (fn [{:keys [db]} _]
   (local-store->)
   {:db (merge db/default-db demo-db)}))

(re-frame/reg-event-fx
 :fail-service
 standard-interceptors
 (fn [{:keys [db]} [service result msg]]
   (let [default-msg (case (:status result)
                       400 "Validation error."
                       422 "Validation error."
                       401 "Couldn't login to service!"
                       403 "Content not authorize for this user"
                       404 "Object does not exist"
                       0   "No internet connection"
                       "Unknown Error")
         icon (case (:status result)
                400 "error"
                422 "error"
                401 "ban-circle"
                403 "disable"
                404 "error"
                0   "offline"
                "error")
         message (or msg
                     (str default-msg ": "
                          (str service)
                          ". "
                          (:detail (:response result))))]
     {:db (update-in db [:loading-refcount] dec)})))

(re-frame/reg-event-fx
 :set-current-route
 standard-interceptors
 (fn [{:keys [db]} [route]]
   ;; The route is a `reitit` object
   (let [title (translate :en (get-in route [:data :title]))]
     (set! (.-title js/document)
           (if (some? title)
             (str (:product-name db) " | " title)
             (:product-name db)))
     {:db (assoc db :current-route route)})))

;;------------------------
;; Global loading

(re-frame/reg-event-db
 :set-loading
 (fn [db [_]]
   (assoc db :loading? true)))

(re-frame/reg-event-db
 :unset-loading
 (fn [db [_]]
   (assoc db :loading? false)))

;;------------------------
;; Global error handling

(re-frame/reg-event-db
 :set-error
 (fn [db [_ error]]
   (assoc db :last-error (str error))))

(re-frame/reg-event-db
 :clear-last-error
 (fn [db [_]]
   (dissoc db :last-error)))

(re-frame/reg-event-db
 :set-interval
 (fn [db [_ key interval callback]]
   (let [interval-id (js/setInterval callback interval)]
     (assoc-in db [:intervals key] interval-id))))

(re-frame/reg-event-db
 :clear-all-interval
 (fn [db [_]]
   (let [interval-ids (->> (get db :intervals)
                           (map (fn [[_interval-key interval-id]] interval-id)))]
     (doall
      (for [interval-id interval-ids]
        (js/clearInterval interval-id))))
   (assoc db :intervals {})))

(re-frame/reg-event-db
 :clear-interval
 (fn [db [_ key]]
   (js/clearInterval (get-in db [:intervals key]))
   (update-in db [:intervals] dissoc key)))

(re-frame/reg-event-fx
 :get-userinfo
 (fn [{:keys [db]} _]
   {:db db
    :dispatch [:fail-get-userinfo]}))

(re-frame/reg-event-db
 :success-get-userinfo
 [(re-frame/path :settings)
  standard-interceptors]
 (fn [settings [result]]
   (if (some? result)
     (-> settings
         (assoc :userinfo result))
     settings)))

(re-frame/reg-event-db
 :fail-get-userinfo
 standard-interceptors
 (fn [db _]
   (assoc db :signed-in? false)))

;;-------------------------
;; Scroll

;; FIXME: Make me a FX
(re-frame/reg-event-fx
 ::scroll-to-element
 standard-interceptors
 (fn [{:keys [db]} [element]]
   (reagent/after-render
    #(when-let [el (dom/getElement element)]
       (.scrollIntoView el #js {:behavior "smooth" :block "center"})))
   nil))

;;-------------------------
;; Timer

(defn dispatch-timer-event
  []
  (let [now (js/Date.)]
    (re-frame/dispatch [:timer now])))

;; call the dispatching function every second
(defonce do-timer (js/setInterval dispatch-timer-event 1000))

(re-frame/reg-event-db
 :timer
 (fn [db [_ new-time]]
   (assoc db :time new-time)))

;;-------------------------
;; App Boot Configuration

;; boot flow as per https://github.com/Day8/re-frame-async-flow-fx
;; :boot-complete needs to be invoked at the end of process (where :halt? true)
;; to render the app.
;; This decision was made to ensure that the booting process complete before
;; the app startup
(defn boot-flow []
  {:id :flow/boot
   :first-dispatch [::auth0/do-auth]
   :rules [{:when :seen?
            :events ::auth0/finish-loading
            :dispatch-n [[::users/get-userinfo]
                         [:set-loading]]}
           {:when :seen?
            :events ::users/get-userinfo-fail
            :dispatch [:boot-complete false]
            :halt? true}
           {:when :seen?
            :events ::users/get-userinfo-success
            :dispatch-n [[::events/list-events-by-organization]
                         [::users/list-users-by-organization]
                         [::google/get-calendar]]}
           {:when :seen-all-of?
            :events [::events/list-events-by-organization-success
                     ::users/list-users-by-organization-success]
            :dispatch [::events/select-default-event]}
           {:when :seen?
            :events ::events/select-default-event
            :dispatch-n [[::rooms/list-rooms-by-event]
                         [::bookings/list-bookings-by-event]
                         [:init-graphql-client]
                         [::bookings/subscribe-bookings]]}
           {:when :seen-all-of?
            :events [::rooms/list-rooms-by-event-success
                     ::bookings/list-bookings-by-event-success]
            :dispatch [:boot-complete true]
            :halt? true}]})

;; Invitation boot is triggered when an invitation link was parsed in the query
;; parameters.
(defn invitation-boot-flow [invitation organization]
  {:id :flow/boot
   :first-dispatch [::auth0/do-auth]
   :rules [{:when :seen?
            :events ::auth0/finish-loading
            :dispatch [::auth0/accept-invitation invitation organization]
            :halt? true}]})

(defn demo-boot-flow []
  {:id :flow/boot
   :first-dispatch [:get-userinfo]
   :rules [{:when :seen?
            :events :fail-get-userinfo
            :dispatch [:boot-complete true]
            :halt? true}]})

;; The initial boot event when loading the app
;; it checks for query parameters than can comes from the invitation links
;; to decide which async flow to use.
;; It also looks at the local storage to load an existing db state
(re-frame/reg-event-fx
 :boot
 [(re-frame/inject-cofx :local-store-settings)]  ;; obtain settings from localstore
 (fn [{:keys [db local-store-settings]} _]
   (let [params (parse-params)
         is-demo? (:is-demo? local-store-settings)
         is-authenticated? (boolean (:auth0/user-data local-store-settings))]
     (cond
       (and (some? params) (some? (:invitation params)) (some? (:organization params)))
       {:async-flow (invitation-boot-flow (:invitation params) (:organization params))}

       (and (some? params) (some? (:code params)) (some? (:state params)))
       {:db db/default-db
        :async-flow (boot-flow)}

       :else
       {:db (-> (if (empty? local-store-settings)
                  db/default-db
                  (if is-demo?
                    (merge db/default-db demo-db)
                    db/default-db)))
        :async-flow (if is-demo?
                      (demo-boot-flow)
                      (boot-flow))}))))

(re-frame/reg-event-fx
 :boot-complete
 [(re-frame/inject-cofx :local-store-settings)]
 (fn [{:keys [db local-store-settings]} [_ success?]]
   (if success?
     {:dispatch [:unset-loading]
      :navigate! [:route.home/home {} {}]
      :db (if (nil? (get-in db [:selected :space-id]))
            (assoc-in db [:selected :event-id] (:id (first (:event-organizer/events db))))
            db)}
     {:dispatch [:unset-loading]
      :navigate! [:route.login/login]})))

;; -------------------------
;; Notifications

(re-frame/reg-fx
 :show-notification!
 (fn [{:keys [title message duration intent]
       :or {duration 5000}
       :as notif}]
   (re-frame/dispatch [:show-notification notif])))

(re-frame/reg-event-db
 :show-notification
 standard-interceptors
 (fn [db [{:keys [title message duration intent]
           :or {duration 5000}
           :as notif}]]
   (let [interval-id (js/setTimeout #(re-frame/dispatch [:hide-notification]) duration)]
     (-> db
         (assoc :notification (assoc notif :is-open true))))))

(re-frame/reg-event-db
 :hide-notification
 standard-interceptors
 (fn [db _]
   (assoc-in db [:notification :is-open] false)))

;; -------------------------
;; User Guide

(re-frame/reg-event-db
 :show-user-guide
 standard-interceptors
 (fn [db [{:keys [page]}]]
   (-> db
       (assoc :user-guide {:is-open? true
                           :page page}))))

(re-frame/reg-event-db
 :hide-user-guide
 standard-interceptors
 (fn [db _]
   (assoc db :user-guide {:is-open? false
                          :page nil})))

;; -------------------------
;; GraphQL

(re-frame/reg-event-fx
 ::connection-init
 dashboard.appsync.internals/interceptors
 (fn [{:keys [db]} _]
   (let [ws (get-in db [:ws :connection])]
     {:re-graph.internals/send-ws [ws {:type "connection_init"}]})))

(re-frame/reg-event-fx
 :init-graphql-client
 standard-interceptors
 (fn [{:keys [db]} _]
   (let [token (:auth0/user-id-token db)]
     {:dispatch [::re-graph/init
                 {:ws
                  {:reconnect-timeout 5000
                   :url (str config/graphql-ws-url
                             "?header=" (js/btoa (->json
                                                  {"host" config/graphql-host
                                                   "Authorization" token}))
                             "&payload=" (js/btoa {}))}}]})))

(re-frame/reg-event-fx
 ::default-graphql-fail
 dashboard.appsync.internals/interceptors
 (fn [{:keys [db]} [res]]
   (let [errors (get-in res [:response :errors])
         status (:status res)]
     (if (= status 401)
       {:db (assoc db :loading-refcount 0)
        :dispatch [::auth/do-logout]
        :show-notification! {:title "GraphQL Authentication Error"
                             :intent :intent/danger
                             :duration 10000
                             :message (clojure.string/join "\n" (map :message errors))}}
       {:db (assoc db :loading-refcount 0)
        :show-notification! {:title "GraphQL Error"
                             :intent :intent/danger
                             :duration 10000
                             :message (clojure.string/join "\n" (map :message errors))}}))))

(re-frame/reg-event-fx
 :send-graphql-request
 standard-interceptors
 (fn [{:keys [db]}
      [{:keys [query variables on-success on-failure]
        :or {variables {}
             on-success []
             on-failure [::default-graphql-fail]}}]]
   (let [token (:auth0/user-id-token db)]
     {:http-xhrio {:method :post
                   :uri config/graphql-http-url
                   :format graphql-request-format
                   :headers {:authorization token}
                   :response-format (ajax/json-response-format {:keywords? true})
                   :params {:query query
                            :variables variables}
                   :on-success on-success
                   :on-failure on-failure}})))

(re-frame/reg-fx
 :graphql-request
 (fn [{:keys [query variables on-success on-failure] :as params}]
   (re-frame/dispatch [:send-graphql-request params])))
